import { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";
import { useNavigate } from "react-router-dom";
import {
  MapPin,
  Trophy,
  AlignLeft,
  Check,
  Search,
  Loader2,
  Tag,
  User,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
  Info,
} from "lucide-react";
import ImageUpload from "../../components/ImageUpload";

const StepWizard = ({ currentStep, totalSteps, titles }) => {
  const progress = ((currentStep - 1) / (totalSteps - 1)) * 100;
  return (
    <div className="mb-8">
      <div className="flex justify-between mb-2">
        {titles.map((title, index) => (
          <span
            key={index}
            className={`text-xs font-bold uppercase tracking-wider ${
              index + 1 <= currentStep ? "text-blue-600" : "text-gray-300"
            }`}
          >
            {title}
          </span>
        ))}
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-blue-600 transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

const InputCard = ({ icon: Icon, label, value, onChange, placeholder, type = "text", error }) => (
  <div>
    <div
      className={`bg-white rounded-xl p-3 flex items-center gap-3 border shadow-sm transition-all focus-within:ring-2 focus-within:ring-blue-100 ${
        error ? "border-red-300 ring-2 ring-red-50" : "border-gray-100"
      }`}
    >
      <div className={`p-2 rounded-lg ${error ? "bg-red-50 text-red-400" : "bg-gray-50 text-gray-400"}`}>
        <Icon size={18} />
      </div>
      <div className="flex-1">
        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
          {label}
        </label>
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full text-gray-900 font-bold text-sm bg-transparent border-none p-0 focus:ring-0 placeholder-gray-300"
        />
      </div>
    </div>
    {error && <p className="text-xs text-red-500 mt-1 pl-1 font-medium">{error}</p>}
  </div>
);

const SelectCard = ({ icon: Icon, label, value, onChange, options, error }) => (
  <div>
    <div
      className={`bg-white rounded-xl p-3 flex items-center gap-3 border shadow-sm transition-all focus-within:ring-2 focus-within:ring-blue-100 ${
        error ? "border-red-300 ring-2 ring-red-50" : "border-gray-100"
      }`}
    >
      <div className={`p-2 rounded-lg ${error ? "bg-red-50 text-red-400" : "bg-gray-50 text-gray-400"}`}>
        <Icon size={18} />
      </div>
      <div className="flex-1 relative">
        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
          {label}
        </label>
        <select
          value={value}
          onChange={onChange}
          className="w-full text-gray-900 font-bold text-sm bg-transparent border-none p-0 focus:ring-0 appearance-none cursor-pointer"
        >
          <option value="">Select...</option>
          {options.map((opt) => (
            <option key={opt.name || opt} value={opt.name || opt}>
              {opt.name || opt}
            </option>
          ))}
        </select>
      </div>
    </div>
    {error && <p className="text-xs text-red-500 mt-1 pl-1 font-medium">{error}</p>}
  </div>
);

export default function CreateTrainer() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [user, setUser] = useState(null);
  const [sports, setSports] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    trainer_name: "",
    sport: "",
    bio: "",
    skills_taught_ids: [],
    trainer_logo: null,
    location_address: "",
    latitude: null,
    longitude: null
  });

  useEffect(() => {
    const init = async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;
      setUser(authUser);

      const { data: profile } = await supabase
        .from("users")
        .select("full_name")
        .eq("id", authUser.id)
        .single();

      if (profile?.full_name) {
        setForm((prev) => ({ ...prev, trainer_name: profile.full_name }));
      }

      const { data } = await supabase.from("development_sports").select("name");
      if (data) setSports(data);

      const { data: existingProgram } = await supabase
        .from("programs")
        .select("id")
        .eq("administrator", authUser.id)
        .maybeSingle();

      if (existingProgram?.id) {
        navigate("/skills-trainer/swot", { replace: true });
      }
    };

    init();
  }, [navigate]);

  useEffect(() => {
    const fetchSkills = async () => {
      if (!form.sport) {
        setSections([]);
        setSelectedSectionId("");
        return;
      }
      const { data: sportData } = await supabase
        .from("development_sports")
        .select("id")
        .eq("name", form.sport)
        .single();

      if (!sportData?.id) return;

      const { data: sectionsData } = await supabase
        .from("development_sections")
        .select("id, name")
        .eq("sport_id", sportData.id)
        .order("name");

      setSections(sectionsData || []);
    };
    fetchSkills();
  }, [form.sport]);

  const handleAddressSearch = async () => {
    if (!form.location_address) return;
    setAddressLoading(true);
    setErrors((prev) => ({ ...prev, location_address: null }));

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${encodeURIComponent(form.location_address)}`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const result = data[0];
        const address = result.address;

        // Validation: Ensure a specific street, road, or building is found
        const hasSpecificAddress = address.road || address.house_number || address.building || address.industrial || address.pedestrian;

        if (!hasSpecificAddress) {
          setErrors((prev) => ({ 
            ...prev, 
            location_address: "Please provide a specific street address. City/State only is not specific enough." 
          }));
          setForm((prev) => ({ ...prev, latitude: null, longitude: null }));
          return;
        }

        setForm((prev) => ({
          ...prev,
          location_address: result.display_name,
          latitude: parseFloat(result.lat),
          longitude: parseFloat(result.lon),
        }));
      } else {
        setErrors((prev) => ({ ...prev, location_address: "Address not found. Please check spelling." }));
      }
    } catch (err) {
      console.error("Geocoding failed", err);
      setErrors((prev) => ({ ...prev, location_address: "Search service unavailable." }));
    } finally {
      setAddressLoading(false);
    }
  };

  const addSkill = () => {
    if (!selectedSectionId) return;
    if (form.skills_taught_ids.includes(selectedSectionId)) return;
    setForm((prev) => ({ ...prev, skills_taught_ids: [...prev.skills_taught_ids, selectedSectionId] }));
  };

  const removeSkill = (skillId) => {
    setForm((prev) => ({
      ...prev,
      skills_taught_ids: prev.skills_taught_ids.filter((s) => s !== skillId),
    }));
  };

  const validateStep = (currentStep) => {
    const nextErrors = {};
    if (currentStep === 1) {
      if (!form.trainer_name) nextErrors.trainer_name = "Trainer name is required";
      if (!form.sport) nextErrors.sport = "Sport is required";
      if (!form.bio) nextErrors.bio = "Background is required";
    }
    if (currentStep === 2) {
      if (form.skills_taught_ids.length === 0) nextErrors.skills_taught = "Select at least one skill";
    }
    if (currentStep === 3) {
      if (!form.location_address) nextErrors.location_address = "Address is required";
      if (!form.latitude) nextErrors.location_address = "Please search and verify a specific street address";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    setSubmitting(true);
    try {
      const locationPoint = `POINT(${form.longitude} ${form.latitude})`;

      const { data: newProgram, error } = await supabase
        .from("programs")
        .insert([{
          name: form.trainer_name,
          description: form.bio,
          sport: form.sport,
          program_type: "Skills Trainer",
          location_address: form.location_address,
          location: locationPoint,
          administrator: user.id,
          created_by: user.id,
          program_logo: form.trainer_logo,
          latitude: form.latitude,
          longitude: form.longitude
        }])
        .select().single();

      if (error) throw error;

      if (form.skills_taught_ids.length > 0) {
        const skillRows = form.skills_taught_ids.map(id => ({
          program_id: newProgram.id,
          section_id: id
        }));
        await supabase.from("trainer_skill_categories").insert(skillRows);
      }

      navigate("/skills-trainer/swot");
    } catch (err) {
      setErrors({ global: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      <div className="bg-white px-6 py-4 shadow-sm border-b border-gray-100 flex justify-center sticky top-0 z-50">
        <h1 className="text-lg font-extrabold text-gray-900 tracking-tight text-center">Create Trainer Profile</h1>
      </div>

      <div className="flex-1 max-w-2xl w-full mx-auto p-6">
        <StepWizard currentStep={step} totalSteps={3} titles={["Identity", "Expertise", "Location"]} />

        {errors.global && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
            <AlertCircle size={18} />
            <span className="text-sm font-bold">{errors.global}</span>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Identity</h2>
              <div className="grid gap-4">
                <InputCard icon={User} label="Trainer Name" value={form.trainer_name} onChange={(e) => setForm({ ...form, trainer_name: e.target.value })} error={errors.trainer_name} />
                <SelectCard icon={Trophy} label="Primary Sport" value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} options={sports} error={errors.sport} />
                <div className={`bg-white rounded-xl p-3 border border-gray-100 flex gap-3 ${errors.bio ? "border-red-300 ring-2 ring-red-50" : ""}`}>
                  <div className="bg-gray-50 p-2 rounded-lg text-gray-400 h-fit"><AlignLeft size={18} /></div>
                  <div className="flex-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Background / Bio</label>
                    <textarea rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="w-full text-gray-900 font-medium text-sm border-none p-0 focus:ring-0 resize-none" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-4"><Tag className="text-blue-500" size={20} /><h2 className="text-lg font-bold text-gray-900">Skills & Profile</h2></div>
              <div className="flex gap-2 mb-4">
                <select value={selectedSectionId} onChange={(e) => setSelectedSectionId(e.target.value)} className="flex-1 bg-gray-50 border border-gray-200 text-sm rounded-xl p-3 outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">Select category</option>
                  {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <button onClick={addSkill} className="px-4 py-2 bg-blue-600 text-white rounded-xl font-bold text-sm">Add</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.skills_taught_ids.map(id => (
                  <div key={id} className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-xs font-bold border border-blue-100">
                    {sections.find(s => String(s.id) === String(id))?.name}
                    <button onClick={() => removeSkill(id)}>×</button>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex flex-col items-center">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Profile Image</h2>
              <ImageUpload bucket="program_logos" currentImage={form.trainer_logo} onUploadComplete={(res) => setForm(p => ({ ...p, trainer_logo: res.fullUrl }))} height="h-24" width="w-24" shape="rounded-full" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Location</h2>
              <div className="flex items-start gap-2 text-xs text-blue-600 bg-blue-50 p-3 rounded-xl mb-4">
                <Info size={16} className="flex-shrink-0" />
                <span>Enter your full street address. Building names or cities alone will not verify.</span>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <InputCard 
                    icon={MapPin} 
                    label="Street Address" 
                    placeholder="123 Example St, City, State" 
                    value={form.location_address} 
                    onChange={(e) => {
                      setForm({ ...form, location_address: e.target.value, latitude: null, longitude: null });
                    }} 
                    error={errors.location_address} 
                  />
                </div>
                <button onClick={handleAddressSearch} disabled={addressLoading} className="h-[52px] w-12 bg-blue-600 text-white rounded-xl flex items-center justify-center">
                  {addressLoading ? <Loader2 className="animate-spin" size={20} /> : <Search size={20} />}
                </button>
              </div>
              {form.latitude && (
                <div className="mt-4 flex items-center gap-2 text-emerald-600 bg-emerald-50 p-3 rounded-xl border border-emerald-100">
                  <Check size={18} />
                  <span className="text-xs font-bold uppercase tracking-wide">Specific Address Verified</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white p-4 border-t border-gray-200 sticky bottom-0 flex justify-center">
        <div className="max-w-2xl w-full flex gap-3">
          {step > 1 && <button onClick={() => setStep(step - 1)} className="px-6 py-3 font-bold text-gray-600">Back</button>}
          <button onClick={step === 3 ? handleSubmit : () => validateStep(step) && setStep(step + 1)} disabled={submitting} className="flex-1 bg-blue-600 text-white font-bold py-4 rounded-2xl shadow-lg">
            {submitting ? "Saving..." : step === 3 ? "Complete Profile" : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}